/**
 * Feb 2026 — Auto-tooltip wrapping for Three Little Birds-generated content.
 *
 * <GlossaryHighlight text="..." /> scans plain text, finds matches
 * against the glossary lookup map (case-insensitive, whole-word), and
 * wraps each first occurrence in an underlined trigger that opens a
 * Popover with the definition + a "Browse all terms →" link.
 *
 * Only the FIRST occurrence of each term per render is wrapped — keeps
 * a long paragraph from looking like a kindergarten textbook.
 *
 * For React-node children (not just strings), this component is a
 * no-op pass-through — strings are the supported input.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { BookOpen, ExternalLink, Link2, Check, Star } from "lucide-react";
import { toast } from "sonner";
import useGlossary, { getGlossaryLookup } from "../lib/useGlossary";
import { recordGlossaryView } from "../lib/glossaryRecent";
import { getGlossaryFavorites, toggleGlossaryFavorite } from "../lib/glossaryFavorites";
import { setGlossaryOrigin } from "../lib/glossaryNav";
import { usePrintMode } from "../context/PrintModeContext";

function buildMatcher(lookup) {
  if (!lookup || lookup.size === 0) return null;
  // Sort by length DESC so "annual recurring revenue" wins over "revenue"
  // (avoids fragmenting a longer term into many short ones).
  const phrases = Array.from(lookup.keys())
    .sort((a, b) => b.length - a.length)
    .map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return new RegExp(`\\b(${phrases.join("|")})\\b`, "gi");
}

function GlossaryTooltipTrigger({ surface, entry }) {
  const [open, setOpen] = useState(false);
  // Jun 2026 · Popover share link — same canonical deep link the
  // Glossary page's copy button produces, so a founder can share a
  // definition straight from the plan text.
  const [copied, setCopied] = useState(false);
  // Jun 2026 · Favorites everywhere — starred terms (same localStorage set
  // as the Glossary page) surface first visually with an amber underline,
  // and can be starred/unstarred straight from the popover.
  const [starred, setStarred] = useState(() => getGlossaryFavorites().includes(entry.term));
  const toggleStar = () => {
    const next = toggleGlossaryFavorite(entry.term);
    setStarred(next.includes(entry.term));
  };
  const slug = entry.term.toLowerCase().replace(/\s+/g, "-");
  const printing = usePrintMode();
  const copyTermLink = async () => {
    const url = `${window.location.origin}/glossary#${slug}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
    toast.success(`Link to "${entry.term}" copied — share it with a co-founder or advisor.`);
  };
  // Full Plan PDF / print export: render the highlighted term as a real
  // link to the online glossary so it stays clickable in the exported PDF.
  if (printing) {
    return (
      <a
        href={`#glossary-appendix-${slug}`}
        onClick={(e) => {
          setGlossaryOrigin(slug, e.currentTarget);
          const target = document.getElementById(`glossary-appendix-${slug}`);
          if (target) {
            e.preventDefault();
            target.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }}
        data-testid={`glossary-term-${slug}`}
        data-glossary-slug={slug}
        className="font-bold underline decoration-dotted decoration-neutral-400 underline-offset-2 text-[#7C5FB0] inline bg-transparent p-0 m-0 border-0 align-baseline"
        style={{ fontFamily: "inherit", fontSize: "inherit", fontStyle: "inherit", lineHeight: "inherit", letterSpacing: "inherit" }}
      >
        {surface}
      </a>
    );
  }
  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        // Jun 2026 · Opening a term popover counts as "looked up" for the
        // Recently-viewed row on the Glossary page.
        if (v) recordGlossaryView(entry.term);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`glossary-term-${entry.term.toLowerCase().replace(/\s+/g, "-")}`}
          className={`font-bold underline decoration-dotted ${starred ? "decoration-amber-500" : "decoration-neutral-400"} underline-offset-2 hover:decoration-neutral-900 hover:text-neutral-900 cursor-help transition-colors inline bg-transparent p-0 m-0 border-0 align-baseline`}
          style={{ fontFamily: "inherit", fontSize: "inherit", fontStyle: "inherit", color: "inherit", lineHeight: "inherit", letterSpacing: "inherit" }}
          aria-label={`Glossary: ${entry.term}`}
        >
          {surface}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={4}
        className="w-80 p-0 border border-neutral-300 rounded-none shadow-lg"
        data-testid={`glossary-popover-${entry.term.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <div className="px-4 py-3 bg-neutral-900 text-white flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-300 mb-0.5">
              {entry.category}
            </div>
            <div className="font-bold text-sm">{entry.term}</div>
          </div>
          <button
            type="button"
            onClick={toggleStar}
            data-testid="glossary-popover-star"
            data-starred={starred ? "true" : "false"}
            title={starred ? `Unstar "${entry.term}"` : `Star "${entry.term}" to keep it at the top of the glossary`}
            aria-label={`${starred ? "Unstar" : "Star"} ${entry.term}`}
            className={`shrink-0 p-0.5 transition-colors ${starred ? "text-amber-400" : "text-neutral-500 hover:text-amber-400"}`}
          >
            <Star className={`w-4 h-4 ${starred ? "fill-amber-400" : ""}`} aria-hidden />
          </button>
        </div>
        <div className="px-4 py-3 text-sm text-neutral-800 leading-relaxed">
          {entry.definition}
        </div>
        <div className="px-4 py-2 border-t border-neutral-200 flex items-center justify-between gap-2 bg-neutral-50">
          <Link
            to="/glossary"
            data-testid="glossary-popover-browse-all"
            className="text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-900 hover:underline inline-flex items-center gap-1 no-underline"
          >
            <BookOpen className="w-3 h-3" />
            Browse all terms
          </Link>
          <button
            type="button"
            onClick={copyTermLink}
            data-testid="glossary-popover-copy-link"
            title={`Copy a direct link to "${entry.term}"`}
            aria-label={`Copy link to ${entry.term}`}
            className={`text-[10px] uppercase tracking-[0.15em] font-bold inline-flex items-center gap-1 transition-colors ${
              copied ? "text-emerald-600" : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            {copied ? <Check className="w-3 h-3" aria-hidden /> : <Link2 className="w-3 h-3" aria-hidden />}
            {copied ? "Copied" : "Copy link"}
          </button>
          {entry.learn_more_url && (
            <a
              href={entry.learn_more_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1 no-underline"
            >
              Learn more <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function GlossaryHighlight({ text, className }) {
  // Subscribe to the glossary so we re-render once it loads
  useGlossary();
  const lookup = getGlossaryLookup();

  const rendered = useMemo(() => {
    if (!text || typeof text !== "string") return text;
    const matcher = buildMatcher(lookup);
    if (!matcher) return text;

    const parts = [];
    const seen = new Set();
    let lastIdx = 0;
    let m;
    matcher.lastIndex = 0;
    while ((m = matcher.exec(text)) !== null) {
      const matched = m[0];
      const key = matched.toLowerCase();
      const entry = lookup.get(key);
      if (!entry) continue;

      const dedupKey = entry.term.toLowerCase();
      if (seen.has(dedupKey)) continue; // wrap only the FIRST occurrence
      seen.add(dedupKey);

      if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
      parts.push(
        <GlossaryTooltipTrigger
          key={`${entry.term}-${m.index}`}
          surface={matched}
          entry={entry}
        />,
      );
      lastIdx = m.index + matched.length;
    }
    if (lastIdx < text.length) parts.push(text.slice(lastIdx));
    return parts.length === 0 ? text : parts;
  }, [text, lookup]);

  return <span className={className} data-testid="glossary-highlight">{rendered}</span>;
}
